import {
  AInput,
  ASwitch,
  ASelect,
  OptionFooter,
  ContainerForm,
} from '../../../../components';
import { Card, Col, Form, Row, Space, Typography } from 'antd';
import {
  mask,
  cellPhoneMask,
  phoneMask,
} from '../../../../shared/utils/functions';
import { schemaProfessional } from '../../schemas/yup.professional.schema';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../../hooks/toast';
import { useNavigate } from 'react-router-dom';
import {
  IProfessional,
  ISpecialty,
  ITerminology,
} from '../../../../shared/interfaces';
import { patchProfessional, postProfessional } from '../../../../http';
import {
  IdcardOutlined,
  UserOutlined,
  PhoneOutlined,
  MobileOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  NumberOutlined,
  StarOutlined,
  PercentageOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

interface IFormDataProfessionalProps {
  professional: IProfessional | null;
  terminologies: ITerminology[];
  specialties: ISpecialty[];
}

const FormDataProfessional: React.FC<IFormDataProfessionalProps> = ({
  professional,
  terminologies,
  specialties,
}) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [name, setName] = useState<string>();
  const [cpf, setCpf] = useState<string>('');
  const [phone, setPhone] = useState<string>();
  const [cellPhone, setCellPhone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [commission, setCommission] = useState<number>(0);
  const [terminologyId, setTerminologyId] = useState<string>();
  const [terminologyRegistry, setTerminologyRegistry] = useState<string>();
  const [specialtyId, setSpecialtyId] = useState<string>();
  const [rqe, setRqe] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);

  const handleEditSubmit = useCallback(async () => {
    setDisabled(true);
    schemaProfessional
      .validate({
        name,
        cpf,
        phone,
        cell_phone: cellPhone,
        email,
        commission: +commission,
        terminology_id: terminologyId,
        terminology_code: terminologyRegistry,
        rqe,
        status,
        specialty_id: specialtyId,
      })
      .then(async (formValidated) => {
        await patchProfessional(professional?.id, formValidated)
          .then(() => {
            addToast({
              type: 'info',
              title: 'Profissional alterado',
              description: 'Dados do profissional alterados com sucesso.',
            });
            setDisabled(false);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: `${error.response.data.error ?? 'Erro ao editar.'}`,
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
        setDisabled(false);
      });
  }, [
    addToast,
    name,
    cpf,
    phone,
    cellPhone,
    email,
    commission,
    terminologyId,
    terminologyRegistry,
    rqe,
    status,
    specialtyId,
  ]);

  const handleCreateSubmit = useCallback(async () => {
    setDisabled(true);
    schemaProfessional
      .validate({
        name,
        cpf,
        phone,
        cell_phone: cellPhone,
        email,
        commission: +commission,
        terminology_id: terminologyId,
        terminology_code: terminologyRegistry,
        specialty_id: specialtyId,
        rqe,
      })
      .then(async (formValidated: any) => {
        await postProfessional(formValidated)
          .then(() => {
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: 'Profissional cadastrado com sucesso',
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: `${
                error.response.data.error ?? 'Erro ao cadastrar.'
              }`,
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
        setDisabled(false);
        return;
      });
  }, [
    addToast,
    name,
    cpf,
    phone,
    cellPhone,
    email,
    commission,
    terminologyId,
    terminologyRegistry,
    rqe,
    specialtyId,
  ]);

  const loadData = useCallback(async () => {
    if (!professional) return;

    setName(professional.name);
    setCpf(professional.cpf);
    setPhone(professional.phone);
    setCellPhone(professional.cell_phone);
    setEmail(professional.email);
    setTerminologyId(professional.terminology_id);
    setTerminologyRegistry(professional.terminology_code);
    setSpecialtyId(professional.specialty_id);
    setRqe(professional.rqe);
    setCommission(professional.commission ?? 0);
    setStatus(!!professional.status);
  }, [professional]);

  useEffect(() => {
    loadData();
  }, [professional]);

  return (
    <ContainerForm>
      <Form layout="vertical" style={{ width: '100%' }}>
        <Card
          size="small"
          title={
            <Space size={6}>
              <IdcardOutlined style={{ color: '#1677ff' }} />
              <span>Identificação</span>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <IdcardOutlined style={{ color: '#8c8c8c' }} />
                    <span>CPF</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="000.000.000-00"
                  value={cpf}
                  maxLength={14}
                  onChange={(e) => setCpf(mask(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={18}>
              <Form.Item
                label={
                  <Space size={6}>
                    <UserOutlined style={{ color: '#8c8c8c' }} />
                    <span>Nome completo</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Informe o nome do profissional"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Card
          size="small"
          title={
            <Space size={6}>
              <PhoneOutlined style={{ color: '#52c41a' }} />
              <span>Contato</span>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={
                  <Space size={6}>
                    <MobileOutlined style={{ color: '#8c8c8c' }} />
                    <span>Celular</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="(00) 00000-0000"
                  value={cellPhone}
                  onChange={(e) => setCellPhone(cellPhoneMask(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={
                  <Space size={6}>
                    <PhoneOutlined style={{ color: '#8c8c8c' }} />
                    <span>Telefone</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="(00) 0000-0000"
                  value={phone}
                  onChange={(e) => setPhone(phoneMask(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={
                  <Space size={6}>
                    <MailOutlined style={{ color: '#8c8c8c' }} />
                    <span>E-mail</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="profissional@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Card
          size="small"
          title={
            <Space size={6}>
              <MedicineBoxOutlined style={{ color: '#722ed1' }} />
              <span>Dados Profissionais</span>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <SafetyCertificateOutlined style={{ color: '#8c8c8c' }} />
                    <span>Terminologia</span>
                  </Space>
                }
                required
              >
                <ASelect
                  placeholder="Selecione a terminologia"
                  value={terminologyId}
                  allowClear
                  onChange={(e) => setTerminologyId(e)}
                  options={terminologies.map((term) => ({
                    label: term.description,
                    value: term.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <NumberOutlined style={{ color: '#8c8c8c' }} />
                    <span>Código de Registro</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Ex: 12345/SP"
                  value={terminologyRegistry}
                  onChange={(e) => setTerminologyRegistry(e.target.value)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Número de registro no conselho (ex: CRO, CRM)
                </Typography.Text>
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <StarOutlined style={{ color: '#8c8c8c' }} />
                    <span>Especialidade</span>
                  </Space>
                }
              >
                <ASelect
                  placeholder="Selecione a especialidade"
                  value={specialtyId}
                  allowClear
                  onChange={(e) => setSpecialtyId(e)}
                  options={specialties.map((term) => ({
                    label: term.description,
                    value: term.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <MedicineBoxOutlined style={{ color: '#8c8c8c' }} />
                    <span>RQE</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="Registro de Qualificação"
                  value={rqe}
                  onChange={(e) => setRqe(e.target.value)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Registro de Qualificação de Especialista
                </Typography.Text>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={6}>
              <Form.Item
                label={
                  <Space size={6}>
                    <PercentageOutlined style={{ color: '#8c8c8c' }} />
                    <span>Comissão (%)</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="0"
                  type="number"
                  value={commission}
                  onChange={(e) => setCommission(+e.target.value)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Percentual de comissão sobre os atendimentos
                </Typography.Text>
              </Form.Item>
            </Col>
            {professional && (
              <Col xs={24} sm={6}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <CheckCircleOutlined style={{ color: '#8c8c8c' }} />
                      <span>Profissional ativo</span>
                    </Space>
                  }
                >
                  <ASwitch
                    checked={status}
                    checkedChildren="Sim"
                    unCheckedChildren="Não"
                    onChange={() => setStatus(!status)}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <OptionFooter
          onClickCancel={() => navigate(-1)}
          onClickOk={professional ? handleEditSubmit : handleCreateSubmit}
          disabled={disabled}
        />
      </Form>
    </ContainerForm>
  );
};

export default FormDataProfessional;
